import { createColumnHelper } from "@tanstack/react-table";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import { NativeSelect, palette } from "lib/miolo";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import { trimEnd } from "lodash";
import numeral from "numeral";
import ClipLoader from "react-spinners/ClipLoader";
import type { IDividendsPaidDetails, IDividendsCorporateAction } from "types/graphqlTypes";

interface IAmericanDividendsPaidTabContetProps {
  data?: IDividendsCorporateAction;
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
}

export default function AmericanDividendsPaidTabContet({
  data,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
}: IAmericanDividendsPaidTabContetProps) {
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();

  if (!data && !filterDividendsByYear) {
    return null;
  }

  const arrayOfColumns = [
    columnHelper.accessor("excludingDate", {
      header: "Excluding Date",
      cell: (props) => formatDateOrReturnHyphen(props.getValue()),
    }),
    columnHelper.accessor("payDate", {
      header: "Pay Date",
      cell: (props) => formatDateOrReturnHyphen(props.getValue()),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("amountPaid", {
      header: "Value ($)",
      cell: (props) => trimEnd(numeral(props.getValue()).format("0.000").replace(".", ","), "0"),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("distributionFrequency", {
      header: "Frequency",
      cell: (props) => getLabelForDividendFrequency(props.getValue()),
      meta: {
        breakpoint: TableBreakpoint.Medium,
      },
    }),
  ];

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-full md:w-52">
        <NativeSelect
          height={48}
          value={filterDividendsByYear ?? ""}
          onChange={(event) => {
            setFilterDividendsByYear(event.target.value ? parseInt(event.target.value, 10) : null);
          }}
          style={{
            borderRadius: "8px",
          }}
        >
          <option value="">Filter By Year</option>
          {data?.availableYears.map((year) => (
            <option key={`selectBoxYear/${year}`} value={year}>
              {year}
            </option>
          ))}
        </NativeSelect>
      </div>

      {loading && (
        <div className="flex w-full items-center justify-center">
          <ClipLoader size={28} color={palette.royalBlue} loading={true} />
        </div>
      )}

      {!loading && (
        <Table<IDividendsPaidDetails>
          columns={arrayOfColumns}
          data={data?.nodes ?? []}
          isOddEven={true}
          isPaginationEnabled={true}
        />
      )}
    </div>
  );
}

function getLabelForDividendFrequency(value?: number | null) {
  switch (value) {
    case 1:
      return "Yearly";
    case 2:
      return "Semester";
    case 4:
      return "Quarterly";
    case 12:
      return "Montlhy ";

    default:
      return "-";
  }
}
